// Sale Opportunity Detail Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { saleOpportunityAPI, saleAPI, horseAPI, crmAPI } from '../../services/api';
import api from '../../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { saleOpportunityDetailQueryFn } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useAuth } from '../../context/AuthContext';
import { fmtDate, fmtDatetime, utcToInputTz, inputTzToUTC } from '../../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { useDateInput } from '../../hooks/useDateInput';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import DeliverableDetailDialog from '../../components/sales/DeliverableDetailDialog';
import FilePreviewDialog from '../../components/sales/FilePreviewDialog';
import BulkUploadDialog from '../../components/sales/BulkUploadDialog';
import BatchReminderDialog from '../../components/sales/BatchReminderDialog';
import OpportunityDeliverablesTab from '../../components/sales/OpportunityDeliverablesTab';
import OpportunityActivityTab from '../../components/sales/OpportunityActivityTab';
import PostSaleTab from '../../components/sales/PostSaleTab';
import { ConfirmEntryDialog, ChargeConfirmDialog, DeleteConfirmDialog, ArchiveConfirmDialog } from '../../components/sales/OpportunityDialogs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit, Save, Gavel, Eye, Stethoscope, FileText, CheckCircle2, Circle, Clock, AlertCircle,
  ChevronRight, User, DollarSign, Building2, Trash2, Pencil, Archive, Timer, BarChart2,
} from 'lucide-react';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';
import { typeLabels, formatCurrency, formatSoldAs } from '../../components/sales/saleConstants';
import OfflineConflictBadge from '../../components/common/OfflineConflictBadge';

// ── Error Boundary — catches render crashes and shows a readable error ─────────
class SaleOpportunityErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 text-center space-y-3">
          <p className="text-destructive font-medium">Something went wrong loading this opportunity.</p>
          <p className="text-xs text-muted-foreground font-mono bg-muted p-2 rounded text-left whitespace-pre-wrap">
            {this.state.error?.message || String(this.state.error)}
          </p>
          <button className="text-sm underline text-primary" aria-label="Go back" onClick={() => window.history.back()}>Go back</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Sale Window ETA components ────────────────────────────────────────────────
const useETA = (opportunityId) => {
  const [eta, setEta] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await api.get(`/sale-window/eta/${opportunityId}`);
        if (!cancelled && res.data?.estimated_arrival) setEta(res.data);
      } catch { /* silent */ }
    };
    fetch();
    const interval = setInterval(fetch, 2 * 60 * 1000); // every 2 min — matches Playwright scrape interval
    return () => { cancelled = true; clearInterval(interval); };
  }, [opportunityId]);
  return eta;
};

// Card version — sits in the Post-Sale / Result row
const SaleWindowETACard = ({ opportunityId, opportunity }) => {
  const eta = useETA(opportunityId);
  const isActual = eta?.phase === 'actual';
  const timeStr = eta?.estimated_arrival
    ? new Date(eta.estimated_arrival).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : null;

  // Withdrawn / out horses never have an estimated ring time
  const isWithdrawn = opportunity?.entry_status === 'withdrawn'
    || opportunity?.result === 'withdrawn'
    || opportunity?.entry_status === 'out'
    || opportunity?.result === 'out';

  if (isWithdrawn) {
    return (
      <Card className="shrink-0">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-muted/40 flex items-center justify-center rounded-sm">
            <Timer className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Ring Time</p>
            <p className="font-serif text-xl font-semibold">—</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isActual) {
    // Hip has gone through the ring — mirror the Result card style
    return (
      <Card className="shrink-0">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
            <Timer className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Ring Time</p>
            <p className="font-serif text-xl font-semibold">{timeStr ?? '—'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Estimated (not yet through ring) — amber style with Beta badge
  return (
    <Card className="shrink-0 border-amber-200 bg-amber-50">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-amber-100 flex items-center justify-center rounded-sm">
          <Timer className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-xs text-muted-foreground">Est. Ring Time</p>
            <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-200 text-amber-800 px-1 py-0.5 rounded">Beta</span>
          </div>
          <p className="font-serif text-xl font-semibold text-amber-700">{timeStr ?? <span className="text-amber-400">TBD</span>}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Stage configuration
const stages = [
  { key: 'presale_create', label: 'Potential Entry', order: 1 },
  { key: 'presale_confirm', label: 'Confirmed Entry', order: 2 },
  { key: 'presale_deliverables', label: 'Tracking Deliverables', order: 3 },
  { key: 'presale_ready', label: 'Ready for Sale', order: 4 },
  { key: 'live_sale', label: 'Live Sale', order: 5 },
  { key: 'post_sale', label: 'Post-Sale', order: 6 },
];

const SaleOpportunityDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const { isViewOnly: isViewOnlyFn, isFarm, userTimezone } = useAuth();
  const viewOnly = isViewOnlyFn();
  const qc = useQueryClient();

  const { data: oppData, isLoading: loading } = useQuery({
    queryKey: QK.saleOpportunity(id),
    queryFn: saleOpportunityDetailQueryFn(id),
    enabled: !!id,
    onError: () => toast.error('Failed to load opportunity'),
  });

  const opportunity          = oppData?.opportunity          ?? null;
  const horse                = oppData?.horse                ?? null;
  const sale                 = oppData?.sale                 ?? null;
  const sessions             = oppData?.sessions             ?? [];
  const confirmationStatus   = oppData?.confirmationStatus   ?? null;
  const saleOwnerDeliverables = oppData?.saleOwnerDeliverables ?? [];
  const owners               = oppData?.owners               ?? [];
  const breezeTime           = oppData?.breezeTime           ?? {};
  const digitalMetrics       = oppData?.digitalMetrics       ?? {};
  const deliverables         = opportunity?.deliverables      ?? [];

  const invalidateOpp = () => {
    qc.invalidateQueries({ queryKey: QK.saleOpportunity(id) });
    qc.invalidateQueries({ queryKey: QK.saleOpportunitiesAll() });
    qc.invalidateQueries({ queryKey: QK.saleOpportunities() });
    // Invalidate historic list — archiving/unarchiving moves items in/out of this view
    qc.invalidateQueries({ queryKey: QK.historicSaleOpportunities(), refetchType: 'all' });
  };

  // ── Form state (synced from query data) ──────────────────────────────────
  const [formData, setFormData] = useState({});
  useEffect(() => { if (opportunity) setFormData(opportunity); }, [opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [breezeForm, setBreezeForm] = useState({ breeze_date: '', ut_time: '', ut_distance: '', gallop_out_time: '', other_notes: '' });
  useEffect(() => {
    if (breezeTime) setBreezeForm({ breeze_date: breezeTime.breeze_date ? breezeTime.breeze_date.split('T')[0] : '', ut_time: breezeTime.ut_time || '', ut_distance: breezeTime.ut_distance || '', gallop_out_time: breezeTime.gallop_out_time || '', other_notes: breezeTime.other_notes || '' });
  }, [oppData?.opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [digitalForm, setDigitalForm] = useState({ catalog_page_views: '', media_engagement: '', watchlist_adds: '' });
  useEffect(() => {
    if (digitalMetrics) setDigitalForm({ catalog_page_views: digitalMetrics.catalog_page_views ?? '', media_engagement: digitalMetrics.media_engagement ?? '', watchlist_adds: digitalMetrics.watchlist_adds ?? '' });
  }, [oppData?.opportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saleDetailsForm, setSaleDetailsForm] = useState({ sold_as: '', entry_status: '', hip_number: '', session_id: '', property_line: '', sale_entry_notes: '' });
  const [extensionForm, setExtensionForm] = useState({ extension_deadline: '', extension_reason: '' });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const { user: currentUser } = useAuth();
  const isSystemAdmin = currentUser?.roles?.includes('system_owner');
  const isTenantAdmin = currentUser?.roles?.includes('tenant_system_admin') || isSystemAdmin;
  const isReadOnly = viewOnly || (opportunity?.archived && !isTenantAdmin);

  const [editingSaleDetails, setEditingSaleDetails] = useState(false);
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);
  const [deliverableDialogOpen, setDeliverableDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [chargeConfirmDialogOpen, setChargeConfirmDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [archiveConfirmDialogOpen, setArchiveConfirmDialogOpen] = useState(false);

  const isImpersonating = !!localStorage.getItem('impersonation_id');
  const [grantExtensionOpen, setGrantExtensionOpen] = useState(false);
  const [savingExtension, setSavingExtension] = useState(false);
  const extensionDeadlineRef = useDateInput(extensionForm.extension_deadline);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [batchReminderDialogOpen, setBatchReminderDialogOpen] = useState(false);
  const [editingFinancials, setEditingFinancials] = useState(false);
  const [editingResultDetails, setEditingResultDetails] = useState(false);
  const [withdrawalModal, setWithdrawalModal] = useState(false);
  const [withdrawalModalCtx, setWithdrawalModalCtx] = useState(null);
  const [keepInCareModal, setKeepInCareModal] = useState(false);
  const [pendingSoldUpdate, setPendingSoldUpdate] = useState(null);
  const [withdrawalModalMode, setWithdrawalModalMode] = useState('edit');
  const [withdrawalForm, setWithdrawalForm] = useState({ withdrawal_date: '', withdrawal_reason: '' });
  const [prevResultValue, setPrevResultValue] = useState(null);
  const [dialogKey, setDialogKey] = useState(0);
  const [editingBreeze, setEditingBreeze] = useState(false);
  const [savingBreeze, setSavingBreeze] = useState(false);
  const [editingDigital, setEditingDigital] = useState(false);
  const [savingDigital, setSavingDigital] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const [confirmForm, setConfirmForm] = useState({
    sold_as: '', date_entered: '', entry_status: 'confirmed', property_line: '', sale_entry_notes: ''
  });

  const dateInputRef = useDateInput(confirmForm.date_entered);

  useEffect(() => {
    if (confirmDialogOpen) {
      setConfirmForm({ sold_as: '', date_entered: '', entry_status: 'confirmed', property_line: '', sale_entry_notes: '' });
      // Fetch credit balance whenever the dialog opens
      const tenantId = currentUser?.tenant_id;
      if (tenantId) {
        api.get(`/billing/sale-entry-credits/${tenantId}`)
          .then(r => setCreditsRemaining(r.data.credits ?? 0))
          .catch(() => setCreditsRemaining(0));
      } else {
        setCreditsRemaining(0);
      }
    }
  }, [confirmDialogOpen]);


  const handleConfirmEntry = async () => {
    if (!confirmForm.date_entered) {
      toast.error('Please fill in the Date Confirmed field');
      return;
    }
    const isKeeneland = sale?.sale_company?.toLowerCase().includes('keeneland');
    if (isKeeneland && !opportunity?.property_line) {
      toast.error('Property Line is required for Keeneland sales. Please add it in the Sale Entry Details card first.');
      return;
    }
    setChargeConfirmDialogOpen(true);
  };

  const handleProceedWithConfirmation = async () => {
    setChargeConfirmDialogOpen(false);
    setSaving(true);
    try {
      const updateData = {
        // stage is NOT advanced here — that happens when deliverables are selected
        date_entered: confirmForm.date_entered,
        entry_status: confirmForm.entry_status || 'confirmed',
        property_line: confirmForm.property_line || opportunity?.property_line || null,
      };
      // sold_as and sale_entry_notes have no input fields in this dialog.
      // Only write them when the confirm form actually provides a value; otherwise
      // omit them entirely so the backend $set never touches pre-existing data.
      if (confirmForm.sold_as) updateData.sold_as = confirmForm.sold_as;
      if (confirmForm.sale_entry_notes) updateData.sale_entry_notes = confirmForm.sale_entry_notes;
      await saleOpportunityAPI.update(id, updateData);
      // Credit is deducted by the backend when stage advances to presale_confirm
      if (opportunity.horse_id) {
        try { await horseAPI.update(opportunity.horse_id, { sale_status: 'Sale Entry Confirmed' }); } catch (e) { /* ignore */ }
      }
      setConfirmDialogOpen(false);
      navigate(`/sales/opportunities/${id}/select-deliverables`);
    } catch (error) {
      toast.error('Failed to confirm entry');
    } finally { setSaving(false); }
  };

  const handleDeleteOpportunity = async () => { setDeleteConfirmDialogOpen(true); };

  const handleConfirmDelete = async () => {
    try {
      await saleOpportunityAPI.delete(id);
      toast.success('Sale opportunity deleted');
      navigate(-1);
    } catch (error) { toast.error('Failed to delete'); }
    setDeleteConfirmDialogOpen(false);
  };

  const handleArchiveOpportunity = async () => {
    try {
      await saleOpportunityAPI.update(id, { archived: true });
      toast.success('Sale opportunity archived');
      invalidateOpp();
    } catch (error) { toast.error('Failed to archive'); }
    setArchiveConfirmDialogOpen(false);
  };

  const handleUnarchiveOpportunity = async () => {
    try {
      await saleOpportunityAPI.update(id, { archived: false });
      toast.success('Sale opportunity unarchived');
      invalidateOpp();
    } catch (error) { toast.error('Failed to unarchive'); }
  };

  const handleChange = (field, value) => { setFormData(prev => ({...prev, [field]: value})); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saleOpportunityAPI.update(id, formData);
      toast.success('Opportunity updated');
      setEditMode(false);
      invalidateOpp();
      // Notify the Active Sales page to refresh this sale's opportunity list
      window.dispatchEvent(new CustomEvent('sale-opportunity-saved', { detail: { sale_id: opportunity?.sale_id } }));
    } catch (error) { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleSaveSaleDetails = async () => {
    setSaving(true);
    try {
      await saleOpportunityAPI.update(id, saleDetailsForm);
      toast.success('Sale details updated');
      setEditingSaleDetails(false);
      invalidateOpp();
      window.dispatchEvent(new CustomEvent('sale-opportunity-saved', { detail: { sale_id: opportunity?.sale_id } }));
    } catch (error) { toast.error('Failed to update sale details'); }
    finally { setSaving(false); }
  };

  const handleEditSaleDetails = () => {
    setSaleDetailsForm({
      sold_as: opportunity?.sold_as || '',
      entry_status: opportunity?.entry_status || '',
      hip_number: opportunity?.hip_number || '',
      session_id: opportunity?.session_id || '',
      property_line: opportunity?.property_line || '',
      sale_entry_notes: opportunity?.sale_entry_notes || '',
    });
    setEditingSaleDetails(true);
  };

  const handleDeliverableClick = (deliverable) => {
    setSelectedDeliverable(deliverable);
    setDeliverableDialogOpen(true);
  };

  const handleUpdateDeliverable = async (deliverableId, data) => {
    try {
      const isSaleOwnerDeliverable = saleOwnerDeliverables.some(d => d.id === deliverableId);
      if (isSaleOwnerDeliverable) {
        await api.put(`/sale-owner-deliverables/${deliverableId}`, data);
      } else {
        await api.put(`/sale-deliverables/${deliverableId}`, data);
      }

      // Invalidate to refresh deliverables + opportunity in one shot
      await invalidateOpp();
      await new Promise(resolve => setTimeout(resolve, 300));
      toast.success('Deliverable updated');
    } catch (error) { throw error; }
  };

  const calculateProgress = () => {
    if (!opportunity) return 0;
    const isPreSaleStage = (opportunity.stage || '').startsWith('presale_');
    if (isPreSaleStage) {
      const preSaleStagesMap = [
        { key: 'presale_create', order: 1 },
        { key: 'presale_deliverables', order: 2 }, { key: 'presale_confirm', order: 2 },
        { key: 'presale_ready', order: 3 }
      ];
      const current = preSaleStagesMap.find(s => s.key === opportunity.stage);
      return current ? (current.order / 3) * 100 : 0;
    }
    const currentStageObj = stages.find(s => s.key === opportunity.stage);
    return currentStageObj ? (currentStageObj.order / stages.length) * 100 : 0;
  };

  // Determine sale phase based on parent sale's dates AND ship-in date
  // Rule: live starts on the EARLIER of first ship-in date or sale start date
  const salePhaseDerived = (() => {
    if (!sale) return { isLiveSale: false, isPostSale: false };

    const toLocalDate = (val) => {
      if (!val) return null;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString('sv'); // YYYY-MM-DD local
    };

    const todayStr = new Date().toLocaleDateString('sv');
    const saleStartStr = toLocalDate(sale.sale_start_date || sale.start_date);
    const saleEndStr   = toLocalDate(sale.sale_end_date   || sale.end_date);

    // Earliest ship-in date across all sessions
    let shipInStr = null;
    for (const s of (Array.isArray(sessions) ? sessions : [])) {
      const d = toLocalDate(s.ship_in_date);
      if (d && (!shipInStr || d < shipInStr)) shipInStr = d;
    }

    // liveStart = earlier of shipIn and saleStart
    let liveStartStr = null;
    if (shipInStr && saleStartStr) liveStartStr = shipInStr < saleStartStr ? shipInStr : saleStartStr;
    else liveStartStr = shipInStr || saleStartStr;

    const hasResult  = opportunity?.result && opportunity.result !== 'pending';
    const afterEnd   = saleEndStr && todayStr > saleEndStr;
    const isPostSale = hasResult || afterEnd;
    const isLiveSale = (liveStartStr ? (todayStr >= liveStartStr && !isPostSale) : false)
      || (opportunity?.stage === 'live_sale' && !isPostSale);

    return { isLiveSale, isPostSale };
  })();
  const isLiveSale = salePhaseDerived.isLiveSale;
  const isPostSale = salePhaseDerived.isPostSale;
  const isPreSale = !isLiveSale && !isPostSale;
  const isNotConfirmed = opportunity?.entry_status !== 'confirmed';

  const handleGrantExtension = async () => {
    if (!extensionForm.extension_deadline || !extensionForm.extension_reason.trim()) {
      toast.error('Both Extension Expiry Date/Time and Reason are required');
      return;
    }
    setSavingExtension(true);
    try {
      // Convert local datetime to UTC ISO
      const utcDeadline = inputTzToUTC(extensionForm.extension_deadline, userTimezone);
      await api.post(`/sale-opportunities/${id}/extension`, {
        extension_deadline: utcDeadline,
        extension_reason: extensionForm.extension_reason.trim(),
      });
      toast.success('Extension granted');
      setGrantExtensionOpen(false);
      // Re-fetch status and opportunity
      await invalidateOpp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to grant extension');
    } finally {
      setSavingExtension(false);
    }
  };

  const handleRevokeExtension = async () => {
    try {
      await api.delete(`/sale-opportunities/${id}/extension`);
      toast.success('Extension revoked');
      setGrantExtensionOpen(false);
      setExtensionForm({ extension_deadline: '', extension_reason: '' });
      await invalidateOpp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke extension');
    }
  };

  const handlePreviewFile = (deliverable, label) => {
    try {
      if (!deliverable?.file_url) { toast.error('File not available for preview'); return; }
      setPreviewFile({ ...deliverable, deliverable_type_label: label });
      setPreviewOpen(true);
    } catch (err) { toast.error('Failed to open preview'); }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  if (!opportunity) {
    return (
      <SidebarLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Opportunity not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </SidebarLayout>
    );
  }

  // Quick-access preview: catalogue page & vet report from deliverables
  const pedigreeDeliverable = deliverables.find(d =>
    (d.deliverable_type_horse === 'catalogue_page' || d.deliverable_type === 'catalogue_page') && d.file_url
  ) || null;
  const vetReportDeliverable = deliverables.find(d =>
    (d.deliverable_type_horse === 'vet_report' || d.deliverable_type === 'vet_report') && d.file_url
  ) || null;

  return (
    <SaleOpportunityErrorBoundary>
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          {/* Row 1: Back button */}
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="px-0 hover:bg-transparent">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          {/* Row 2: Opportunity name + Archive/Delete (desktop: same row right; mobile: below) */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div className="min-w-0">
              <h1 className="font-serif text-2xl font-semibold leading-tight flex items-center gap-2 flex-wrap">
                {opportunity.opportunity_name}
                <OfflineConflictBadge record={opportunity} entityType="sale-opportunities" onDismissed={invalidateOpp} />
              </h1>
              <p className="text-base font-bold text-foreground mt-0.5">Hip #{opportunity.hip_number || 'TBD'}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:pt-1">
              {isNotConfirmed && !isReadOnly && (
                <>
                  {/* Confirm Sale Entry — shown/enabled based on deadline rules */}
                  {(confirmationStatus?.allowed || confirmationStatus === null) ? (
                    <Button
                      onClick={() => { setDialogKey(prev => prev + 1); setConfirmDialogOpen(true); }}
                      className="bg-primary hover:bg-primary/90 text-white"
                      data-testid="confirm-sale-entry-btn"
                    >
                      Confirm Sale Entry
                    </Button>
                  ) : (
                    <span
                      title="The deadline for this sale has passed. If you have an exceptional circumstance, please contact support."
                      className="cursor-not-allowed"
                      data-testid="confirm-sale-entry-btn-disabled"
                    >
                      <Button disabled className="pointer-events-none opacity-50">
                        Confirm Sale Entry
                      </Button>
                    </span>
                  )}
                  {/* Grant Extension — only visible to System Owners during impersonation */}
                  {isImpersonating && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      onClick={() => setGrantExtensionOpen(true)}
                      data-testid="grant-extension-btn"
                    >
                      {opportunity?.extension_deadline ? 'Edit Extension' : 'Grant Extension'}
                    </Button>
                  )}
                </>
              )}
              {!isReadOnly && !opportunity.archived && (
                <Button variant="outline" size="sm" onClick={() => setArchiveConfirmDialogOpen(true)}>
                  Archive Sale Opportunity
                </Button>
              )}
              {isSystemAdmin && (
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600" onClick={handleDeleteOpportunity}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Live Sale / Post-Sale Indicator Card */}
        {(isLiveSale || isPostSale) && (
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <Card className="bg-primary text-white sm:flex-1">
              <CardHeader>
                <CardTitle className="text-base">{isLiveSale ? 'Live Sale' : 'Post-Sale'}</CardTitle>
              </CardHeader>
            </Card>
            {isLiveSale && !isNotConfirmed && (
              <SaleWindowETACard opportunityId={id} opportunity={opportunity} />
            )}
            {!isNotConfirmed && (
            <Card className={`shrink-0 ${opportunity.result === 'withdrawn' ? 'border-2 border-red-500' : ''}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center ${opportunity.result === 'withdrawn' ? 'bg-red-100' : 'bg-primary/10'}`}>
                  <Gavel className={`w-5 h-5 ${opportunity.result === 'withdrawn' ? 'text-red-500' : 'text-primary'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Result</p>
                  <p className="font-serif text-xl font-semibold">{opportunity.result?.toUpperCase() || 'Pending'}</p>
                </div>
              </CardContent>
            </Card>
            )}
          </div>
        )}

        {/* Pre-Sale Progress Card + quick-access buttons side by side */}
        <div className="flex items-stretch gap-3">
          <Card className="flex-1">
            <CardHeader>
              <CardTitle className="text-base">Pre-Sale Progress</CardTitle>
            </CardHeader>
            <CardContent>
              {opportunity.archived && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Archive className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-900 font-medium">
                      This sale opportunity has been archived {!isSystemAdmin && '(Read-Only)'}
                    </span>
                  </div>
                  {isTenantAdmin && (
                    <Button variant="outline" size="sm" className="border-green-600 text-green-600 hover:bg-green-50 h-7 text-xs" onClick={handleUnarchiveOpportunity}>
                      Unarchive
                    </Button>
                  )}
                </div>
              )}
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-1">
                  <span className={`text-[11px] leading-tight ${opportunity.stage === 'presale_create' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>Create</span>
                  <span className={`text-[11px] leading-tight text-center ${(opportunity.stage === 'presale_deliverables' || opportunity.stage === 'presale_confirm') ? 'text-primary font-medium' : 'text-muted-foreground'}`}>Track Deliverables</span>
                  <span className={`text-[11px] leading-tight text-right ${opportunity.stage === 'presale_ready' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>Ready for Sale</span>
                </div>
                <Progress value={calculateProgress()} className="h-2" />
              </div>
            </CardContent>
          </Card>

          {/* Stacked Pedigree / Vet Report buttons — only shown when files exist and entry is confirmed */}
          {(pedigreeDeliverable || vetReportDeliverable) && !isNotConfirmed && (
            <div className="flex flex-col gap-2 flex-shrink-0">
              {pedigreeDeliverable && (
                <Button
                  variant="outline"
                  style={{ borderRadius: '0.75rem' }}
                  className="flex-1 w-24 text-xs border-primary/30 text-primary hover:bg-primary hover:text-white flex flex-col items-center justify-center gap-1.5 h-auto py-3 px-2"
                  onClick={() => handlePreviewFile(pedigreeDeliverable, 'Catalogue Page')}
                  data-testid="view-pedigree-btn"
                  title="View Catalogue Page"
                >
                  <FileText className="w-4 h-4" />
                  Pedigree
                </Button>
              )}
              {vetReportDeliverable && (
                <Button
                  variant="outline"
                  style={{ borderRadius: '0.75rem' }}
                  className="flex-1 w-24 text-xs border-primary/30 text-primary hover:bg-primary hover:text-white flex flex-col items-center justify-center gap-1.5 h-auto py-3 px-2"
                  onClick={() => handlePreviewFile(vetReportDeliverable, 'Vet Report')}
                  data-testid="view-vet-report-btn"
                  title="View Vet Report"
                >
                  <Stethoscope className="w-4 h-4" />
                  Vet Report
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Sale Entry Details — shown from creation */}
        {true && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
              <CardTitle className="text-sm font-semibold">Sale Entry Details</CardTitle>
              {!isReadOnly && (
                !editingSaleDetails ? (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleEditSaleDetails}><Pencil className="w-3.5 h-3.5" /></Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditingSaleDetails(false)}>Cancel</Button>
                    <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" onClick={handleSaveSaleDetails} disabled={saving}>
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                  </div>
                )
              )}
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Sale</span>
                  <p className="font-medium">{opportunity?.opportunity_name?.split(' - ')[0] || sale?.sale_name || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Horse</span>
                  <p className="font-medium">{horse?.system_name || horse?.name || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">To Be Sold As</span>
                  {editingSaleDetails ? (
                    <Select value={saleDetailsForm.sold_as} onValueChange={(value) => setSaleDetailsForm({...saleDetailsForm, sold_as: value})}>
                      <SelectTrigger className="mt-0.5 h-7 text-xs"><SelectValue placeholder="Select type..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weanling">Weanling</SelectItem>
                        <SelectItem value="yearling">Yearling</SelectItem>
                        <SelectItem value="2yo">2YO</SelectItem>
                        <SelectItem value="hora">HORA</SelectItem>
                        <SelectItem value="broodmare">Broodmare</SelectItem>
                        <SelectItem value="broodmare_prospect">Broodmare Prospect</SelectItem>
                        <SelectItem value="racing_broodmare_prospect">Racing/Broodmare Prospect</SelectItem>
                        <SelectItem value="racing_prospect">Racing Prospect</SelectItem>
                        <SelectItem value="stallion">Stallion</SelectItem>
                        <SelectItem value="stallion_prospect">Stallion Prospect</SelectItem>
                        <SelectItem value="racing_stallion_prospect">Racing/Stallion Prospect</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">{formatSoldAs(opportunity?.sold_as)}</p>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Entry Status</span>
                  {editingSaleDetails ? (
                    <div className="flex items-center gap-1 mt-0.5">
                    <Select value={saleDetailsForm.entry_status} onValueChange={(value) => {
                        if (value === 'withdrawn') {
                          // Intercept: open modal to collect withdrawal date + reason
                          setWithdrawalForm({ withdrawal_date: saleDetailsForm.withdrawal_date || '', withdrawal_reason: saleDetailsForm.withdrawal_reason || '' });
                          setWithdrawalModalCtx('entry_status');
                          setWithdrawalModalMode('edit');
                          setWithdrawalModal(true);
                          return;
                        }
                        setSaleDetailsForm({...saleDetailsForm, entry_status: value});
                      }}>
                      <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select status..." /></SelectTrigger>
                      <SelectContent>
                        {sale?.sale_company?.toLowerCase().includes('fasig') ? (
                          <>
                            <SelectItem value="nominated">Nominated</SelectItem>
                            <SelectItem value="still_under_consideration">Still Under Consideration</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="recommended_another_sale">Recommended Another Sale</SelectItem>
                            <SelectItem value="withdrawn">Withdrawn</SelectItem>
                            {(!isNotConfirmed || isImpersonating) && <SelectItem value="confirmed">Confirmed</SelectItem>}
                          </>
                        ) : (
                          <>
                            <SelectItem value="entered">Entered</SelectItem>
                            <SelectItem value="withdrawn">Withdrawn</SelectItem>
                            {(!isNotConfirmed || isImpersonating) && <SelectItem value="confirmed">Confirmed</SelectItem>}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {saleDetailsForm.entry_status === 'withdrawn' && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary"
                        title="View/edit withdrawal details"
                        onClick={() => {
                          setWithdrawalForm({ withdrawal_date: saleDetailsForm.withdrawal_date || '', withdrawal_reason: saleDetailsForm.withdrawal_reason || '' });
                          setWithdrawalModalCtx('entry_status');
                          setWithdrawalModalMode('view');
                          setWithdrawalModal(true);
                        }}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <p className="font-medium">{opportunity?.entry_status ? opportunity.entry_status.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '-'}</p>
                      {opportunity?.entry_status === 'withdrawn' && (opportunity.withdrawal_date || opportunity.withdrawal_reason) && (
                        <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-primary"
                          title="View withdrawal details"
                          onClick={() => {
                            setWithdrawalForm({ withdrawal_date: opportunity.withdrawal_date ? opportunity.withdrawal_date.split('T')[0] : '', withdrawal_reason: opportunity.withdrawal_reason || '' });
                            setWithdrawalModalCtx('entry_status');
                            setWithdrawalModalMode('view');
                            setWithdrawalModal(true);
                          }}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Hip #</span>
                  {editingSaleDetails ? (
                    <Input type="number" value={saleDetailsForm.hip_number} onChange={(e) => setSaleDetailsForm({...saleDetailsForm, hip_number: e.target.value})} className="mt-0.5 h-7 text-xs" placeholder="Enter hip number" />
                  ) : (
                    <p className="font-medium">#{opportunity?.hip_number || 'TBD'}</p>
                  )}
                </div>
                {!sale?.is_digital_sale && (
                <div>
                  <span className="text-muted-foreground">Session Date</span>
                  {editingSaleDetails ? (
                    <Select value={saleDetailsForm.session_id} onValueChange={(value) => setSaleDetailsForm({...saleDetailsForm, session_id: value})}>
                      <SelectTrigger className="mt-0.5 h-7 text-xs"><SelectValue placeholder="Select session..." /></SelectTrigger>
                      <SelectContent>
                        {sessions.length === 0 ? (
                          <SelectItem value="none" disabled>No sessions available</SelectItem>
                        ) : (
                          sessions.map((session) => (
                            <SelectItem key={session.id} value={session.id}>
                              {session.name} - {fmtDate(session.date)}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">
                      {opportunity?.session_id && sessions.find(s => s.id === opportunity.session_id)
                        ? sessions.find(s => s.id === opportunity.session_id).name
                        : '-'}
                    </p>
                  )}
                </div>
                )}
                {!sale?.is_digital_sale && (() => {
                  const session = sessions.find(s => s.id === (saleDetailsForm.session_id || opportunity?.session_id));
                  const shipIn = session?.ship_in_date;
                  if (!shipIn) return null;
                  const formatted = new Date(`${String(shipIn).split('T')[0]}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  return (
                    <div key="ship-in">
                      <span className="text-muted-foreground">Ship In Date</span>
                      <p className="font-medium">{formatted}</p>
                    </div>
                  );
                })()}
                <div className="col-span-2">
                  <span className="text-muted-foreground">Property Line</span>
                  {editingSaleDetails ? (
                    <Input value={saleDetailsForm.property_line || ''} onChange={(e) => setSaleDetailsForm({...saleDetailsForm, property_line: e.target.value})} className="mt-0.5 h-7 text-xs" placeholder="Identifies the seller or legal owner of the horse at the time of the sale" />
                  ) : (
                    <p className="font-medium">{opportunity?.property_line || '-'}</p>
                  )}
                </div>
                <div className="col-span-3">
                  <span className="text-muted-foreground">Sale Entry Notes</span>
                  {editingSaleDetails ? (
                    <Textarea value={saleDetailsForm.sale_entry_notes || ''} onChange={(e) => setSaleDetailsForm({...saleDetailsForm, sale_entry_notes: e.target.value})} className="mt-0.5 text-xs" placeholder="Additional notes about this sale entry..." rows={2} />
                  ) : (
                    <p className="font-medium">{opportunity?.sale_entry_notes || '-'}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue={tabParam || (isPreSale ? 'deliverables' : (isPostSale && !isNotConfirmed && opportunity.result && opportunity.result !== 'pending' ? 'post-sale' : (isNotConfirmed ? 'deliverables' : 'activity')))} className="space-y-4"
          onValueChange={(v) => { if (v === 'activity') setActivityKey(k => k + 1); }}>
          <div className="overflow-x-auto pb-0.5">
          <TabsList className="whitespace-nowrap w-max">
            {(isLiveSale || isPostSale) ? (
              <>
                {isPostSale && (!isNotConfirmed || opportunity.result === 'withdrawn') && opportunity.result && opportunity.result !== 'pending' && (
                  <TabsTrigger value="post-sale"><FileText className="w-4 h-4 mr-2" /> Post-Sale Actions</TabsTrigger>
                )}
                {!isNotConfirmed && (
                  <TabsTrigger value="activity"><Eye className="w-4 h-4 mr-2" /> Activity</TabsTrigger>
                )}
                {!isNotConfirmed && sale?.is_undertack_show && (
                  <TabsTrigger value="breeze-times"><Timer className="w-4 h-4 mr-2" /> Breeze Times</TabsTrigger>
                )}
                {sale?.is_digital_sale && (
                  <TabsTrigger value="digital-metrics"><BarChart2 className="w-4 h-4 mr-2" /> Digital Metrics</TabsTrigger>
                )}
                {!isNotConfirmed && (
                  <TabsTrigger value="financials"><DollarSign className="w-4 h-4 mr-2" /> Results</TabsTrigger>
                )}
                <TabsTrigger value="deliverables"><FileText className="w-4 h-4 mr-2" /> Deliverables</TabsTrigger>
                <TabsTrigger value="horse"><HorseshoeIcon className="w-4 h-4 mr-2" /> Horse</TabsTrigger>
                <TabsTrigger value="owners"><User className="w-4 h-4 mr-2" /> Owners ({owners.length})</TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="deliverables"><FileText className="w-4 h-4 mr-2" /> Deliverables</TabsTrigger>
                <TabsTrigger value="horse"><HorseshoeIcon className="w-4 h-4 mr-2" /> Horse</TabsTrigger>
                <TabsTrigger value="owners"><User className="w-4 h-4 mr-2" /> Owners ({owners.length})</TabsTrigger>
              </>
            )}
          </TabsList>
          </div>

          {/* Post-Sale Tab */}
          <TabsContent value="post-sale">
            <PostSaleTab opportunityId={id} opportunity={opportunity} sale={sale} isReadOnly={isReadOnly} />
          </TabsContent>

          {/* Deliverables Tab */}
          <TabsContent value="deliverables">
            <OpportunityDeliverablesTab
              id={id}
              opportunity={opportunity}
              deliverables={deliverables}
              saleOwnerDeliverables={saleOwnerDeliverables}
              owners={owners}
              isReadOnly={isReadOnly}
              onDeliverableClick={handleDeliverableClick}
              onPreviewFile={handlePreviewFile}
              onBulkUpload={() => setBulkUploadDialogOpen(true)}
              onBatchReminder={() => setBatchReminderDialogOpen(true)}
            />
          </TabsContent>

          {/* Horse Tab */}
          <TabsContent value="horse">
            {horse ? (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    <div className="w-32 h-32 bg-muted flex items-center justify-center shrink-0">
                      {horse.main_photo_url ? (
                        <img src={horse.main_photo_url} alt={horse.system_name} className="w-full h-full object-cover" />
                      ) : (
                        <HorseshoeIcon className="w-16 h-16 text-muted-foreground/30" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl font-semibold">{horse.system_name || horse.name}</h3>
                      <p className="text-muted-foreground">{horse.age} {horse.gender} &bull; {horse.color?.replace(/_/g, ' ')}</p>
                      {(() => {
                        const flags = [];
                        if (horse.vices_cribber) flags.push('Cribber');
                        if (horse.vices_pawing) flags.push('Pawing');
                        if (horse.vices_stall_walking) flags.push('Stall Walking');
                        if (horse.vices_wall_kicking) flags.push('Wall Kicking');
                        if (horse.vices_weaving) flags.push('Weaving');
                        if (horse.vices_wood_chewing) flags.push('Wood Chewing');
                        if (horse.other_issues_eye_certification) flags.push('Eye Certification');
                        if (horse.other_issues_surgery) flags.push('Surgery');
                        if (horse.other_issues_regumate) flags.push('Regumate');
                        if (horse.other_issues_shoes_for_sale) flags.push('Shoes for the Sale');
                        return flags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {flags.map((flag, i) => (
                              <Badge key={i} variant="destructive" className="text-xs py-0 px-1.5">{flag}</Badge>
                            ))}
                          </div>
                        ) : null;
                      })()}
                      <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate(`/horses/${horse.id}`)}>
                        View Full Profile <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <HorseshoeIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                  <p className="text-muted-foreground">Horse not found</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Owners Tab */}
          <TabsContent value="owners">
            <Card>
              <CardHeader><CardTitle className="font-serif text-lg">Ownership</CardTitle></CardHeader>
              <CardContent>
                {owners.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <User className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>No owners linked to this opportunity</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {owners.map((owner) => (
                      <div key={owner.id} className="flex items-center justify-between p-4 border border-border hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/crm/accounts/${owner.id}`)}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                            {owner.crm_type === 'organization' ? <Building2 className="w-5 h-5 text-muted-foreground" /> : <User className="w-5 h-5 text-muted-foreground" />}
                          </div>
                          <div>
                            <p className="font-medium">{owner.system_name}</p>
                            <p className="text-xs text-muted-foreground">{owner.person_industry_role || owner.organization_industry_role || 'Owner'}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <OpportunityActivityTab key={activityKey} opportunityId={id} saleId={opportunity.sale_id} />
          </TabsContent>

          {/* Breeze Times Tab */}
          {sale?.is_undertack_show && (
          <TabsContent value="breeze-times">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Timer className="w-4 h-4 text-primary" /> Breeze Times
                </CardTitle>
                {!isReadOnly && !editingBreeze && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingBreeze(true)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
                {editingBreeze && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                      setBreezeForm({ breeze_date: breezeTime?.breeze_date ? breezeTime.breeze_date.split('T')[0] : '', ut_time: breezeTime?.ut_time || '', ut_distance: breezeTime?.ut_distance || '', gallop_out_time: breezeTime?.gallop_out_time || '', other_notes: breezeTime?.other_notes || '' });
                      setEditingBreeze(false);
                    }}>Cancel</Button>
                    <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" disabled={savingBreeze}
                      onClick={async () => {
                        setSavingBreeze(true);
                        try {
                          const res = await api.put(`/breeze-times/${id}`, breezeForm);
                          qc.setQueryData(QK.saleOpportunity(id), prev => prev ? { ...prev, breezeTime: res.data } : prev);
                          setEditingBreeze(false);
                          toast.success('Breeze times saved');
                        } catch { toast.error('Failed to save breeze times'); }
                        finally { setSavingBreeze(false); }
                      }}>
                      {savingBreeze ? <><Clock className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : <><Save className="w-3 h-3 mr-1" /> Save</>}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-4">
                  {/* Breeze Date */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Breeze Date</Label>
                    {editingBreeze ? (
                      <Input type="date" value={breezeForm.breeze_date} onChange={e => setBreezeForm(p => ({...p, breeze_date: e.target.value}))} className="h-8 text-sm" />
                    ) : (
                      <p className="text-sm font-medium">
                        {breezeTime?.breeze_date
                          ? new Date(breezeTime.breeze_date.split('T')[0] + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : <span className="text-muted-foreground italic">Not recorded</span>}
                      </p>
                    )}
                  </div>
                  {/* UT Time */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">UT Time</Label>
                    {editingBreeze ? (
                      <Input value={breezeForm.ut_time} onChange={e => setBreezeForm(p => ({...p, ut_time: e.target.value}))} placeholder="e.g. :21.4" className="h-8 text-sm" />
                    ) : (
                      <p className="text-sm font-medium">{breezeTime?.ut_time || <span className="text-muted-foreground italic">Not recorded</span>}</p>
                    )}
                  </div>

                  {/* UT Distance */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">UT Distance</Label>
                    {editingBreeze ? (
                      <Select value={breezeForm.ut_distance} onValueChange={v => setBreezeForm(p => ({...p, ut_distance: v}))}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select distance..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1 furlong (1/8 mile)">1 furlong (1/8 mile)</SelectItem>
                          <SelectItem value="2 furlongs (1/4 mile)">2 furlongs (1/4 mile)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-medium">{breezeTime?.ut_distance || <span className="text-muted-foreground italic">Not recorded</span>}</p>
                    )}
                  </div>

                  {/* Gallop-Out Time */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Gallop-Out Times</Label>
                    {editingBreeze ? (
                      <Textarea value={breezeForm.gallop_out_time} onChange={e => setBreezeForm(p => ({...p, gallop_out_time: e.target.value}))} placeholder="e.g. :35.2, :36.1 — enter multiple times separated by commas or new lines" rows={3} className="text-sm" />
                    ) : (
                      <p className="text-sm font-medium whitespace-pre-wrap">{breezeTime?.gallop_out_time || <span className="text-muted-foreground italic">Not recorded</span>}</p>
                    )}
                  </div>

                  {/* Other Notes */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Other Notes</Label>
                    {editingBreeze ? (
                      <Textarea value={breezeForm.other_notes} onChange={e => setBreezeForm(p => ({...p, other_notes: e.target.value}))} placeholder="Any additional notes..." rows={3} className="text-sm" />
                    ) : (
                      <p className="text-sm">{breezeTime?.other_notes || <span className="text-muted-foreground italic">No notes</span>}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Digital Metrics Tab */}
          {sale?.is_digital_sale && (
          <TabsContent value="digital-metrics">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-primary" /> Digital Metrics
                </CardTitle>
                {!isReadOnly && !editingDigital && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingDigital(true)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
                {editingDigital && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                      setDigitalForm({ catalog_page_views: digitalMetrics?.catalog_page_views ?? '', media_engagement: digitalMetrics?.media_engagement ?? '', watchlist_adds: digitalMetrics?.watchlist_adds ?? '' });
                      setEditingDigital(false);
                    }}>Cancel</Button>
                    <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" disabled={savingDigital}
                      onClick={async () => {
                        setSavingDigital(true);
                        try {
                          const res = await api.put(`/digital-metrics/${id}`, digitalForm);
                          qc.setQueryData(QK.saleOpportunity(id), prev => prev ? { ...prev, digitalMetrics: res.data } : prev);
                          setEditingDigital(false);
                          toast.success('Digital metrics saved');
                        } catch { toast.error('Failed to save'); }
                        finally { setSavingDigital(false); }
                      }}>
                      {savingDigital ? <><Clock className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : <><Save className="w-3 h-3 mr-1" /> Save</>}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { key: 'catalog_page_views', label: 'Catalog Page Views', icon: Eye },
                    { key: 'media_engagement',   label: 'Media Engagement',   icon: FileText },
                    { key: 'watchlist_adds',      label: 'Watchlist Adds',     icon: CheckCircle2 },
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Icon className="w-3 h-3" /> {label}
                      </Label>
                      {editingDigital ? (
                        <Input type="number" min="0" value={digitalForm[key]}
                          onChange={e => setDigitalForm(p => ({...p, [key]: e.target.value}))}
                          className="h-8 text-sm" placeholder="0" />
                      ) : (
                        <p className="text-2xl font-semibold font-mono">
                          {digitalMetrics?.[key] != null ? digitalMetrics[key] : <span className="text-lg text-muted-foreground">—</span>}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {digitalMetrics?.updated_at && !editingDigital && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Last updated {fmtDatetime(digitalMetrics.updated_at, userTimezone)}
                    {digitalMetrics.updated_by && ` by ${digitalMetrics.updated_by}`}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Results Tab */}
          <TabsContent value="financials">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg">Sale Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Top Row: Reserve Price + Net Proceeds (if sold) */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 flex items-center justify-center shrink-0">
                      <DollarSign className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Reserve Price</p>
                      {editingFinancials ? (
                        <div className="flex items-center gap-2">
                          <Input type="number" className="h-8 w-40" placeholder="Enter reserve price" value={formData.reserve_price ?? opportunity.reserve_price ?? ''} onChange={(e) => handleChange('reserve_price', parseFloat(e.target.value) || null)} />
                          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditingFinancials(false)}>Cancel</Button>
                          <Button size="sm" className="h-8 text-xs bg-primary hover:bg-primary/90 text-white" onClick={async () => {
                            setSaving(true);
                            try {
                              await saleOpportunityAPI.update(id, { reserve_price: formData.reserve_price ?? opportunity.reserve_price });
                              toast.success('Reserve price saved');
                              setEditingFinancials(false);
                              invalidateOpp();
                            } catch { toast.error('Failed to save'); }
                            finally { setSaving(false); }
                          }} disabled={saving}>Save</Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="font-serif text-xl font-semibold">{formatCurrency(opportunity.reserve_price, sale?.sale_currency)}</p>
                          {!isReadOnly && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingFinancials(true)}><Pencil className="w-3 h-3" /></Button>}
                        </div>
                      )}
                    </div>
                  </div>
                  {(formData.result ?? opportunity.result) === 'sold' && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 flex items-center justify-center shrink-0">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Net Proceeds</p>
                        <p className="font-serif text-xl font-semibold text-primary">{formatCurrency(opportunity.net_proceeds, sale?.sale_currency)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Result Dropdown */}
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Result</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                    {isReadOnly ? (
                      <p className="font-serif text-xl font-semibold">{opportunity.result ? opportunity.result.replace(/_/g, ' ').toUpperCase() : 'Pending'}</p>
                    ) : (
                      <Select
                        value={formData.result ?? opportunity.result ?? 'pending'}
                        onValueChange={async (v) => {
                          if (v === 'withdrawn') {
                            // Intercept: don't auto-save. Open modal to collect date + reason first.
                            setPrevResultValue(formData.result ?? opportunity.result ?? 'pending');
                            handleChange('result', v);
                            setWithdrawalForm({ withdrawal_date: opportunity.withdrawal_date ? opportunity.withdrawal_date.split('T')[0] : '', withdrawal_reason: opportunity.withdrawal_reason || '' });
                            setWithdrawalModalCtx('result');
                            setWithdrawalModalMode('edit');
                            setWithdrawalModal(true);
                            return;
                          }
                          // Issue 4: Intercept "sold" on FarmFolio tenants — ask "Keep in care?"
                          if (v === 'sold' && isFarm) {
                            handleChange('result', v);
                            setPendingSoldUpdate(v);
                            setKeepInCareModal(true);
                            return;
                          }
                          handleChange('result', v);
                          if (v !== 'pending') setEditingResultDetails(true);
                          try {
                            await saleOpportunityAPI.update(id, { result: v });
                            invalidateOpp();
                          } catch { toast.error('Failed to update result'); }
                        }}
                      >
                        <SelectTrigger className="w-48 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="sold">Sold</SelectItem>
                          <SelectItem value="rna">RNA</SelectItem>
                          <SelectItem value="withdrawn">Withdrawn</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    </div>
                    <div className="flex items-center gap-2">
                    {!isReadOnly && (formData.result ?? opportunity.result) && (formData.result ?? opportunity.result) !== 'pending' && !editingResultDetails && (
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditingResultDetails(true)}>Edit</Button>
                    )}
                    {editingResultDetails && (
                      <>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditingResultDetails(false)}>Cancel</Button>
                        <Button size="sm" className="h-7 px-2 text-xs bg-primary hover:bg-primary/90 text-white" onClick={async () => {
                          setSaving(true);
                          try {
                            const fields = ['sale_price','signed_ticket_as','sale_price_notes','payment_method','rna_hammer_price','withdrawal_date','withdrawal_reason','sale_company_chargeback','sale_company_entry_fee','sale_company_commission','sale_company_lien','sale_company_other_amount','sale_company_other_notes'];
                            const updates = {};
                            fields.forEach(f => { if (formData[f] !== undefined) updates[f] = formData[f]; });
                            if (Object.keys(updates).length > 0) {
                              await saleOpportunityAPI.update(id, updates);
                              toast.success('Saved');
                            }
                            setEditingResultDetails(false);
                            invalidateOpp();
                          } catch { toast.error('Failed to save'); }
                          finally { setSaving(false); }
                        }} disabled={saving}>
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                      </>
                    )}
                    </div>
                  </div>
                </div>

                {/* Result-specific fields */}
                {(() => {
                  const currentResult = formData.result ?? opportunity.result;
                  if (!currentResult || currentResult === 'pending') return null;
                  const editing = editingResultDetails;

                  return (
                    <>
                      <div className="pt-1">

                        {currentResult === 'withdrawn' && (
                          editing ? (
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Withdrawal Date</Label><Input type="date" value={formData.withdrawal_date ?? opportunity.withdrawal_date ?? ''} className={formData.withdrawal_date || opportunity.withdrawal_date ? 'date-filled' : 'text-muted-foreground'} onChange={(e) => handleChange('withdrawal_date', e.target.value)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Reason</Label><Input value={formData.withdrawal_reason ?? opportunity.withdrawal_reason ?? ''} onChange={(e) => handleChange('withdrawal_reason', e.target.value)} /></div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-6">
                              <div><p className="text-sm text-muted-foreground">Withdrawal Date</p><p className="text-sm font-medium">{opportunity.withdrawal_date ? fmtDate(opportunity.withdrawal_date) : '-'}</p></div>
                              <div><p className="text-sm text-muted-foreground">Reason</p><p className="text-sm font-medium">{opportunity.withdrawal_reason || '-'}</p></div>
                            </div>
                          )
                        )}

                        {currentResult === 'rna' && (
                          editing ? (
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Hammer Price ($)</Label><Input type="number" value={formData.rna_hammer_price ?? opportunity.rna_hammer_price ?? ''} onChange={(e) => handleChange('rna_hammer_price', parseFloat(e.target.value) || null)} /></div>
                            </div>
                          ) : (
                            <div><p className="text-sm text-muted-foreground">Hammer Price</p><p className="text-sm font-medium">{formatCurrency(opportunity.rna_hammer_price, sale?.sale_currency)}</p></div>
                          )
                        )}

                        {currentResult === 'sold' && (
                          editing ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Sale Price ($)</Label><Input type="number" value={formData.sale_price ?? opportunity.sale_price ?? ''} onChange={(e) => handleChange('sale_price', parseFloat(e.target.value) || null)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Signed Ticket Buyer</Label><Input value={formData.signed_ticket_as ?? opportunity.signed_ticket_as ?? ''} onChange={(e) => handleChange('signed_ticket_as', e.target.value)} /></div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Payment Method</Label>
                                <Select value={formData.payment_method ?? opportunity.payment_method ?? ''} onValueChange={(v) => handleChange('payment_method', v)}>
                                  <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                                  <SelectContent><SelectItem value="wire">Wire Transfer</SelectItem><SelectItem value="ach">ACH</SelectItem><SelectItem value="check">Check</SelectItem></SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Notes</Label><Input value={formData.sale_price_notes ?? opportunity.sale_price_notes ?? ''} onChange={(e) => handleChange('sale_price_notes', e.target.value)} /></div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                              <div><p className="text-sm text-muted-foreground">Sale Price</p><p className="text-sm font-medium text-primary">{formatCurrency(opportunity.sale_price, sale?.sale_currency)}</p></div>
                              <div><p className="text-sm text-muted-foreground">Signed Ticket Buyer</p><p className="text-sm font-medium">{opportunity.signed_ticket_as || '-'}</p></div>
                              <div><p className="text-sm text-muted-foreground">Payment Method</p><p className="text-sm font-medium capitalize">{opportunity.payment_method?.replace(/_/g, ' ') || '-'}</p></div>
                              {opportunity.sale_price_notes && <div><p className="text-sm text-muted-foreground">Notes</p><p className="text-sm font-medium">{opportunity.sale_price_notes}</p></div>}
                            </div>
                          )
                        )}
                      </div>

                      {/* Sale Company Fees - for Sold, RNA, and Withdrawn */}
                      {(currentResult === 'sold' || currentResult === 'rna' || currentResult === 'withdrawn') && (
                        <div className="pt-4">
                          <p className="text-sm font-semibold mb-3">Sale Company Fees</p>
                          {editing ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Charge Back ($)</Label><Input type="number" value={formData.sale_company_chargeback ?? opportunity.sale_company_chargeback ?? ''} onChange={(e) => handleChange('sale_company_chargeback', parseFloat(e.target.value) || 0)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Entry Fee ($)</Label><Input type="number" value={formData.sale_company_entry_fee ?? opportunity.sale_company_entry_fee ?? ''} onChange={(e) => handleChange('sale_company_entry_fee', parseFloat(e.target.value) || 0)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Commission ($)</Label><Input type="number" value={formData.sale_company_commission ?? opportunity.sale_company_commission ?? ''} onChange={(e) => handleChange('sale_company_commission', parseFloat(e.target.value) || 0)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Liens ($)</Label><Input type="number" value={formData.sale_company_lien ?? opportunity.sale_company_lien ?? ''} onChange={(e) => handleChange('sale_company_lien', parseFloat(e.target.value) || 0)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Other ($)</Label><Input type="number" value={formData.sale_company_other_amount ?? opportunity.sale_company_other_amount ?? ''} onChange={(e) => handleChange('sale_company_other_amount', parseFloat(e.target.value) || 0)} /></div>
                              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Fee Notes</Label><Input value={formData.sale_company_other_notes ?? opportunity.sale_company_other_notes ?? ''} onChange={(e) => handleChange('sale_company_other_notes', e.target.value)} /></div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div><p className="text-xs text-muted-foreground">Charge Back</p><p className="text-sm font-medium">{formatCurrency(opportunity.sale_company_chargeback, sale?.sale_currency)}</p></div>
                              <div><p className="text-xs text-muted-foreground">Entry Fee</p><p className="text-sm font-medium">{formatCurrency(opportunity.sale_company_entry_fee, sale?.sale_currency)}</p></div>
                              <div><p className="text-xs text-muted-foreground">Commission</p><p className="text-sm font-medium">{formatCurrency(opportunity.sale_company_commission, sale?.sale_currency)}</p></div>
                              <div><p className="text-xs text-muted-foreground">Liens</p><p className="text-sm font-medium">{formatCurrency(opportunity.sale_company_lien, sale?.sale_currency)}</p></div>
                              <div><p className="text-xs text-muted-foreground">Other</p><p className="text-sm font-medium">{formatCurrency(opportunity.sale_company_other_amount, sale?.sale_currency)}</p></div>
                              {opportunity.sale_company_other_notes && <div><p className="text-xs text-muted-foreground">Notes</p><p className="text-sm font-medium">{opportunity.sale_company_other_notes}</p></div>}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Reserve Price edit is now inline above */}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* File Preview Dialog */}
        {previewFile && (
          <FilePreviewDialog
            open={previewOpen}
            onClose={() => { setPreviewOpen(false); setPreviewFile(null); }}
            file={previewFile}
          />
        )}

        {/* Deliverable Detail Dialog */}
        {selectedDeliverable && (
          <DeliverableDetailDialog
            open={deliverableDialogOpen}
            onClose={() => { setDeliverableDialogOpen(false); setSelectedDeliverable(null); }}
            deliverable={selectedDeliverable}
            tenantId={opportunity.tenant_id}
            horseId={opportunity.horse_id}
            deliverableTypeKey={selectedDeliverable.deliverable_type_horse || selectedDeliverable.deliverable_type_owner || selectedDeliverable.deliverable_type}
            sale={sale}
            isReadOnly={isReadOnly}
            deliverableType={{
              label: (() => {
                const key = selectedDeliverable.deliverable_type_horse || selectedDeliverable.deliverable_type_owner || selectedDeliverable.deliverable_type;
                return typeLabels[key] || key;
              })()
            }}
            onUpdate={handleUpdateDeliverable}
            onRefresh={async () => {
              await invalidateOpp();
              // Re-fetch this specific deliverable so the open dialog shows updated data
              const delivId = selectedDeliverable.id;
              const isOwner = saleOwnerDeliverables.some(d => d.id === delivId);
              try {
                if (isOwner) {
                  const res = await api.get(`/sale-owner-deliverables?sale_id=${opportunity.sale_id}`);
                  const updated = (res.data || []).find(d => d.id === delivId);
                  if (updated) setSelectedDeliverable(updated);
                } else {
                  const oppRes = await saleOpportunityAPI.get(id);
                  const updated = (oppRes.data.deliverables || []).find(d => d.id === delivId);
                  if (updated) setSelectedDeliverable(updated);
                }
              } catch (e) { /* silently ignore — dialog will reflect new data on next open */ }
            }}
          />
        )}

        {/* Grant Extension Dialog — System Owner (impersonation) only */}
        <Dialog open={grantExtensionOpen} onOpenChange={setGrantExtensionOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-amber-700">
                {opportunity?.extension_deadline ? 'Edit Confirmation Extension' : 'Grant Confirmation Extension'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-xs text-muted-foreground">
                Granting an extension allows this client to confirm their entry past the standard deadline.
                Both fields are required and are stored in the audit log.
              </p>
              {opportunity?.extension_deadline && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                  Current extension expires: <strong>{fmtDatetime(opportunity.extension_deadline, userTimezone)}</strong>
                  <br />Reason: {opportunity.extension_reason}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Extension Expiry Date &amp; Time <span className="text-red-500">*</span></Label>
                <Input
                  ref={extensionDeadlineRef}
                  type="datetime-local"
                  value={extensionForm.extension_deadline}
                  onChange={e => setExtensionForm(f => ({ ...f, extension_deadline: e.target.value }))}
                  data-testid="extension-deadline-input"
                />
                <p className="text-xs text-muted-foreground">Enter the date and time in your local timezone.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Reason for Extension <span className="text-red-500">*</span></Label>
                <Textarea
                  value={extensionForm.extension_reason}
                  onChange={e => setExtensionForm(f => ({ ...f, extension_reason: e.target.value }))}
                  placeholder="e.g. Client staff emergency — primary contact unavailable prior to deadline"
                  rows={3}
                  data-testid="extension-reason-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              {opportunity?.extension_deadline && (
                <Button variant="ghost" className="text-red-500 hover:text-red-700 mr-auto" onClick={handleRevokeExtension} data-testid="revoke-extension-btn">
                  Revoke Extension
                </Button>
              )}
              <Button variant="outline" onClick={() => setGrantExtensionOpen(false)}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleGrantExtension}
                disabled={savingExtension || !extensionForm.extension_deadline || !extensionForm.extension_reason.trim()}
                data-testid="save-extension-btn"
              >
                {savingExtension ? 'Saving…' : 'Save Extension'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Withdrawal Details Modal */}
        <Dialog open={withdrawalModal} onOpenChange={(open) => {
          if (!open) {
            if (withdrawalModalCtx === 'result' && withdrawalModalMode === 'edit' && prevResultValue !== null) {
              handleChange('result', prevResultValue);
            }
            setWithdrawalModal(false);
            setWithdrawalModalMode('edit');
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{withdrawalModalMode === 'view' ? 'Withdrawal Details' : 'Withdrawal Details Required'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {withdrawalModalMode === 'view' ? (
                <>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Withdrawal Date</p>
                    <p className="text-sm font-medium">{withdrawalForm.withdrawal_date ? fmtDate(withdrawalForm.withdrawal_date + 'T00:00:00') : '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Reason</p>
                    <p className="text-sm font-medium">{withdrawalForm.withdrawal_reason || '—'}</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Please provide a withdrawal date and reason before marking this entry as withdrawn.
                    Both fields are required.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Withdrawal Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      ref={el => { if (el) el.classList.toggle('date-filled', !!withdrawalForm.withdrawal_date); }}
                      value={withdrawalForm.withdrawal_date}
                      onChange={e => setWithdrawalForm(f => ({ ...f, withdrawal_date: e.target.value }))}
                      data-testid="withdrawal-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Reason <span className="text-red-500">*</span></Label>
                    <Input
                      value={withdrawalForm.withdrawal_reason}
                      onChange={e => setWithdrawalForm(f => ({ ...f, withdrawal_reason: e.target.value }))}
                      placeholder="e.g. Horse not ready, owner decision…"
                      data-testid="withdrawal-reason-input"
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter className="gap-2">
              {withdrawalModalMode === 'view' ? (
                <>
                  {/* Pencil edit button bottom-left */}
                  <Button variant="ghost" size="icon" className="mr-auto text-muted-foreground hover:text-primary"
                    onClick={() => setWithdrawalModalMode('edit')}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={() => { setWithdrawalModal(false); setWithdrawalModalMode('edit'); }}>Close</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => {
                    if (withdrawalModalCtx === 'result' && prevResultValue !== null) handleChange('result', prevResultValue);
                    setWithdrawalModal(false);
                    setWithdrawalModalMode('edit');
                  }}>Cancel</Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    disabled={!withdrawalForm.withdrawal_date || !withdrawalForm.withdrawal_reason.trim()}
                    data-testid="withdrawal-confirm-btn"
                    onClick={async () => {
                      if (!withdrawalForm.withdrawal_date || !withdrawalForm.withdrawal_reason.trim()) {
                        toast.error('Both withdrawal date and reason are required');
                        return;
                      }
                      if (withdrawalModalCtx === 'result') {
                        setSaving(true);
                        try {
                          await saleOpportunityAPI.update(id, {
                            result: 'withdrawn',
                            withdrawal_date: withdrawalForm.withdrawal_date,
                            withdrawal_reason: withdrawalForm.withdrawal_reason.trim(),
                          });
                          toast.success('Result updated to Withdrawn');
                          setEditingResultDetails(true);
                          invalidateOpp();
                        } catch { toast.error('Failed to update result'); }
                        finally { setSaving(false); }
                      } else {
                        setSaleDetailsForm(prev => ({
                          ...prev,
                          entry_status: 'withdrawn',
                          withdrawal_date: withdrawalForm.withdrawal_date,
                          withdrawal_reason: withdrawalForm.withdrawal_reason.trim(),
                        }));
                        toast.info('Withdrawal date and reason added. Click Save to apply.');
                      }
                      setWithdrawalModal(false);
                      setWithdrawalModalMode('edit');
                    }}
                  >
                    Confirm Withdrawal
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Issue 4: Keep in care? dialog — shown when FarmFolio tenant marks a horse as sold */}
        <Dialog open={keepInCareModal} onOpenChange={setKeepInCareModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Keep in Care?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground py-2">
              This horse is being marked as sold. Should it remain in your care (FarmFolio) and keep its active pregnancy records?
            </p>
            <DialogFooter className="flex gap-2 justify-end">
              <Button variant="outline" onClick={async () => {
                setKeepInCareModal(false);
                // "No" — proceed with normal sold automation (pauses pregnancy)
                setEditingResultDetails(true);
                try { await saleOpportunityAPI.update(id, { result: pendingSoldUpdate, keep_in_care: false }); invalidateOpp(); }
                catch { toast.error('Failed to update result'); }
                setPendingSoldUpdate(null);
              }}>No — remove from care</Button>
              <Button className="bg-primary hover:bg-primary/90 text-white" onClick={async () => {
                setKeepInCareModal(false);
                // "Yes" — mark sold but skip breeding/pregnancy cascade
                setEditingResultDetails(true);
                try { await saleOpportunityAPI.update(id, { result: pendingSoldUpdate, keep_in_care: true }); invalidateOpp(); }
                catch { toast.error('Failed to update result'); }
                setPendingSoldUpdate(null);
              }}>Yes — keep in care</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Confirm Entry Dialog */}
        <ConfirmEntryDialog
          open={confirmDialogOpen}
          onOpenChange={(open) => {
            setDialogKey(prev => prev + 1);
            setConfirmDialogOpen(open);
          }}
          dialogKey={dialogKey}
          confirmForm={confirmForm}
          setConfirmForm={setConfirmForm}
          sale={sale}
          horse={horse}
          saving={saving}
          onConfirm={handleConfirmEntry}
          dateInputRef={dateInputRef}
          creditsRemaining={creditsRemaining ?? 0}
        />

        <ChargeConfirmDialog open={chargeConfirmDialogOpen} onOpenChange={setChargeConfirmDialogOpen} saving={saving} onProceed={handleProceedWithConfirmation} />

        {/* Bulk Upload Dialog */}
        <BulkUploadDialog open={bulkUploadDialogOpen} onClose={() => setBulkUploadDialogOpen(false)} saleOpportunityId={id} horseDeliverables={deliverables} ownerDeliverables={saleOwnerDeliverables} onUploadComplete={invalidateOpp} />

        {/* Batch Reminder Dialog */}
        <BatchReminderDialog open={batchReminderDialogOpen} onClose={() => setBatchReminderDialogOpen(false)} saleOpportunityId={id} />

        <DeleteConfirmDialog open={deleteConfirmDialogOpen} onOpenChange={setDeleteConfirmDialogOpen} onConfirm={handleConfirmDelete} />
        <ArchiveConfirmDialog open={archiveConfirmDialogOpen} onOpenChange={setArchiveConfirmDialogOpen} onArchive={handleArchiveOpportunity} />
      </div>
    </SidebarLayout>
    </SaleOpportunityErrorBoundary>
  );
};

export default SaleOpportunityDetailPage;
